import { Controller } from "react-hook-form";
import { useEffect } from "react";
import he from "he";
import { IOrganizationDetails } from "../../../core/api/organizations/types";
import useTranslations from "../../../core/i18n/useTranslations";
import {
  useObjectForm,
  useObjectFormRef,
} from "../../../core/SaveObjectContext/hooks";
import TextInput from "../../../ui-lib/components/Inputs/TextInput";
import { Row, Spacer, TwoColGrid } from "../../../components/Layout/Layout";
import Divider from "../../../ui-lib/components/Divider/Divider";
import {
  validateEmailIfExists,
  validateEmergencyPhoneNumberIfExists,
  validatePhoneNumberIfExists,
} from "../../../core/helpers/validation";
import useUser from "../../../core/user/useUser";
import TextArea from "../../../ui-lib/components/Inputs/Textarea";
import CollapsibleBlock from "../../../ui-lib/components/CollapsibleBlock/CollapsibleBlock";
import { AddressRow } from "../AddressRow";
import { TelephonesRow } from "../TelephonesRow";
import { BlockUserCostCenterRow } from "../BlockUserCostCenterRow";

import styles from "./OrganizationsGeneral.module.css";

export const OrganizationGeneralForm = ({
  defaultValues,
  onSubmit,
}: {
  defaultValues?: IOrganizationDetails;
  onSubmit: (values: IOrganizationDetails) => Promise<void>;
}) => {
  const t = useTranslations();
  const formRef = useObjectFormRef();
  const user = useUser();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    trigger,
    setValue,
  } = useObjectForm<IOrganizationDetails>({
    defaultValues,
    shouldUnregister: true,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit(async (values) => {
        await onSubmit(values);
        reset(values);
      })}
      className={styles.detailsForm}
    >
      <Row align="start">
        <Controller
          name="name"
          control={control}
          rules={{
            required: true,
            minLength: 1,
            maxLength: 60,
          }}
          render={({ field }) => (
            <TextInput
              required
              inputRef={field.ref}
              label={t("Common:name_label")}
              placeholder={t("Organizations:org_col_name")}
              value={field.value}
              onChange={(e) => {
                setValue(field.name, e.target.value);
                trigger(field.name);
              }}
              validationError={
                errors?.name && t("Errors:input_organization_name")
              }
            />
          )}
        />
        <Spacer size={32} />
        <Controller
          name="organizationNumber"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t("Organizations:organization_number")}
              placeholder={t("Common:digit_number")}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Row>

      <Spacer size={16} />

      <Row className="w-100" align="start">
        <Controller
          name="customerNumber"
          control={control}
          rules={{
            validate: (value) => {
              if (value && value.length <= 5) {
                return validateEmergencyPhoneNumberIfExists(value);
              }
              return validatePhoneNumberIfExists(value);
            },
          }}
          render={({ field }) => (
            <TextInput
              inputRef={field.ref}
              label={t("Common:labels_phone_number")}
              placeholder="+46123456789"
              description={
                !errors.customerNumber
                  ? t("Common:telephone_placeholder")
                  : undefined
              }
              value={field.value}
              onChange={field.onChange}
              validationError={
                errors?.customerNumber &&
                t("Errors:ValidationHint_TelephoneNumber")
              }
            />
          )}
        />
        <Spacer size={32} />
        <Controller
          name="name"
          control={control}
          render={() => (
            <TextInput
              disabled
              label={
                defaultValues?.parentOrganizationName
                  ? t("Common:belongs_to")
                  : t("Organizations:reseller_name")
              }
              value={
                defaultValues?.parentOrganizationName ??
                defaultValues?.sellerName
              }
            />
          )}
        />
      </Row>
      <AddressRow control={control} />

      <Spacer size={16} />

      <Row align="start">
        <Controller
          name="city"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t("Common:labels_city")}
              placeholder={t("Common:enter_city")}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Spacer size={32} />
        <Controller
          name="country"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t("Common:labels_country")}
              placeholder={t("Common:enter_country")}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Row>

      <Spacer size={32} />
      <Row>
        <Controller
          name="telefax"
          control={control}
          rules={{
            validate: (input: string | undefined) =>
              !input || input.length < 2048,
          }}
          render={({ field }) => (
            <TextArea
              inputRef={field.ref}
              label={t("Organizations:organization_info")}
              placeholder={t("Organizations:organization_info_placeholder")}
              value={he.decode(field.value ?? "")}
              onChange={field.onChange}
              validationError={
                errors?.telefax && t("Errors:ValidationHint_OrganizationInfo")
              }
            />
          )}
        />
      </Row>

      <Spacer size={32} />

      <Divider />

      <Spacer size={32} />

      <h2>{t("Organizations:contact_person_detail")}</h2>

      <Spacer size={16} />

      <Row align="start">
        <Controller
          name="contactPerson"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t("Common:name_label")}
              placeholder={t("Common:contact_person_name")}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Spacer size={32} />
        <Controller
          name="emailAddress"
          control={control}
          rules={{
            maxLength: 60,
            validate: validateEmailIfExists,
          }}
          render={({ field }) => (
            <TextInput
              inputRef={field.ref}
              label={t("Common:email")}
              placeholder={t("Common:placeholder_email")}
              value={field.value}
              onChange={field.onChange}
              validationError={
                errors?.emailAddress && t("Errors:input_email_format")
              }
            />
          )}
        />
      </Row>

      <Spacer size={16} />

      <TwoColGrid>
        <Controller
          name="contactPersonAccessCode"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t("Organizations:contact_person_access_code")}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </TwoColGrid>

      <Spacer size={16} />

      <TelephonesRow control={control} errors={errors} />

      <Spacer size={32} />

      <Divider />

      <Spacer size={32} />

      <CollapsibleBlock withScroll title={t("Common:advanced_options")}>
        <>
          <Spacer size={16} />

          <TwoColGrid>
            {user.hasAccess("UserPrice") && (
              <Controller
                name="defaultPrice"
                control={control}
                render={({ field }) => (
                  <TextInput
                    label={t("Organizations:default_price")}
                    type="number"
                    value={field.value}
                    onChange={field.onChange}
                    renderIcon={() => <span className={styles.priceIcon} />}
                  />
                )}
              />
            )}
          </TwoColGrid>

          <Spacer size={16} />

          <Row align="start">
            <Controller
              name="integrationSnsArn"
              control={control}
              render={({ field }) => (
                <TextInput
                  label={t("Common:labels_sns_arn")}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("Organizations:sns_arn_placeholder")}
                />
              )}
            />
            <Spacer size={32} />
            <Controller
              name="integrationAdditionalInfo"
              control={control}
              render={({ field }) => (
                <TextInput
                  label={t("Common:labels_sns_arn_additional_info")}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Row>
          <Spacer size={16} />
          <BlockUserCostCenterRow control={control} />
        </>
      </CollapsibleBlock>
    </form>
  );
};
